import { components } from "@certn/api/generated-schemas/client-portal";
import { BasicButton } from "@certn/cascadia-react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { EnrollCaseMonitoringModal } from "../../monitoring/components/EnrollCaseMonitoringModal";
import {
  getEligibleChecksForCase,
  getMonitoringCheckTypesForCase,
  hasMonitoringActivityEnrollmentForCase,
} from "../../monitoring/monitoringEligibleChecks";
import { useCanEnrollInMonitoring } from "../../monitoring/useCanEnrollInMonitoring";
import { useCanViewMonitoring } from "../../monitoring/useCanViewMonitoring";
import {
  useEligibleApplicantForCase,
  useMonitoringApplicantDetail,
  useRefreshDashboardApplicants,
  useRefreshEligibleApplicants,
  useRefreshMonitoringActivityLog,
} from "../../monitoring/useMonitoringApi";

interface Props {
  caseShortId: string;
  caseChecks?: readonly components["schemas"]["CaseChecksContent"][];
  subjectId: string;
  status?: components["schemas"]["ClientPortalOverallStatus"];
  decision?: components["schemas"]["ClientPortalDecision"];
  promptEnroll?: boolean;
  promptEligibilityRefreshing?: boolean;
  onPromptHandled?: () => void;
}

export function CaseMonitoringButton({
  caseShortId,
  caseChecks = [],
  subjectId,
  status,
  decision,
  promptEnroll = false,
  promptEligibilityRefreshing = false,
  onPromptHandled,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientPortalEnableContinuousMonitoring } = useFlags();
  const canViewMonitoring = useCanViewMonitoring();
  const canEnroll = useCanEnrollInMonitoring();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const refreshEligible = useRefreshEligibleApplicants();
  const refreshDashboard = useRefreshDashboardApplicants();
  const refreshActivityLog = useRefreshMonitoringActivityLog();

  const monitoringEnabled =
    clientPortalEnableContinuousMonitoring &&
    canViewMonitoring &&
    status?.name === "COMPLETE";

  const meetsCriteria = decision?.name === "MEETS_CRITERIA";

  const {
    data: eligibleData,
    isLoading: eligibleLoading,
    isValidating,
  } = useEligibleApplicantForCase(
    subjectId,
    caseShortId,
    monitoringEnabled && meetsCriteria
  );
  const {
    data: applicantDetail,
    isLoading: enrollmentLoading,
    isValidating: enrollmentValidating,
    mutate: refreshApplicant,
  } = useMonitoringApplicantDetail(subjectId, monitoringEnabled);

  const eligibleChecks = useMemo(
    () => getEligibleChecksForCase(eligibleData, subjectId, caseShortId),
    [eligibleData, subjectId, caseShortId]
  );

  const monitoringCheckTypesForCase = useMemo(
    () =>
      getMonitoringCheckTypesForCase(
        eligibleChecks,
        caseChecks,
        applicantDetail?.enrollments
      ),
    [eligibleChecks, caseChecks, applicantDetail?.enrollments]
  );

  const showMonitoringActivity = useMemo(
    () =>
      hasMonitoringActivityEnrollmentForCase(
        applicantDetail?.enrollments,
        caseShortId,
        monitoringCheckTypesForCase
      ),
    [applicantDetail?.enrollments, caseShortId, monitoringCheckTypesForCase]
  );

  const eligibilitySettled = !eligibleLoading && !isValidating;
  const enrollmentSettled = !enrollmentLoading && !enrollmentValidating;

  const canShowEnrollModal =
    monitoringEnabled &&
    canEnroll &&
    meetsCriteria &&
    !showMonitoringActivity &&
    eligibleChecks.length > 0;
  const shouldOpenModal =
    isModalOpen && meetsCriteria && eligibleChecks.length > 0;

  useEffect(() => {
    if (!isModalOpen || canShowEnrollModal) {
      return;
    }
    if (isValidating || enrollmentValidating || promptEligibilityRefreshing) {
      return;
    }
    setIsModalOpen(false);
    onPromptHandled?.();
  }, [
    isModalOpen,
    canShowEnrollModal,
    isValidating,
    enrollmentValidating,
    promptEligibilityRefreshing,
    onPromptHandled,
  ]);

  useEffect(() => {
    if (!promptEnroll) {
      return;
    }
    if (!monitoringEnabled || !meetsCriteria || showMonitoringActivity) {
      onPromptHandled?.();
      return;
    }
    if (eligibilitySettled && enrollmentSettled && eligibleChecks.length > 0) {
      setIsModalOpen(true);
      return;
    }
    if (
      eligibilitySettled &&
      eligibleChecks.length === 0 &&
      !promptEligibilityRefreshing
    ) {
      onPromptHandled?.();
    }
  }, [
    promptEnroll,
    monitoringEnabled,
    meetsCriteria,
    showMonitoringActivity,
    eligibilitySettled,
    enrollmentSettled,
    eligibleChecks.length,
    promptEligibilityRefreshing,
    onPromptHandled,
  ]);

  if (!monitoringEnabled) {
    return null;
  }

  if (showMonitoringActivity) {
    return (
      <BasicButton
        onPress={() => navigate(`/monitoring/${subjectId}`)}
        testID="case-details-monitoring-activity-button"
      >
        {t("Monitoring activity")}
      </BasicButton>
    );
  }

  if (!enrollmentSettled && !isModalOpen) {
    return null;
  }

  if (!meetsCriteria || eligibleChecks.length === 0) {
    return null;
  }

  function closeModal() {
    setIsModalOpen(false);
    onPromptHandled?.();
  }

  return (
    <>
      <BasicButton
        onPress={() => setIsModalOpen(true)}
        testID="case-details-start-monitoring-button"
      >
        {t("Start monitoring")}
      </BasicButton>

      {shouldOpenModal && (
        <EnrollCaseMonitoringModal
          checks={eligibleChecks}
          onClose={closeModal}
          onSuccess={async () => {
            void Promise.all([
              refreshEligible(),
              refreshApplicant(),
              refreshDashboard(),
              refreshActivityLog(subjectId),
            ]).catch(() => {});
          }}
        />
      )}
    </>
  );
}
